// ─── fieldKeys — stable dotted path convention + entity→area mapping ────────
// Server and client both use this to form consistent keys that match up
// between the diff helper's output and the inline <FieldChangeMarker>
// placements on the client. Keep it framework-free and side-effect-free.

/**
 * Build a stable dotted path. Examples:
 *   makeFieldKey("month", "2026-04", "target")
 *     → "month.2026-04.target"
 *   makeFieldKey("product", "prod_42", "payout", { mktId: "amazon" })
 *     → "product.amazon.prod_42.payout"
 *   makeFieldKey("brand", null, "availableToday")
 *     → "brand.availableToday"
 */
export function makeFieldKey(entityType, entityId, field, extra = {}) {
  const parts = [entityType];
  if (extra.mktId) parts.push(extra.mktId);
  if (extra.orderId) parts.push(extra.orderId);
  if (entityId != null && entityId !== "") parts.push(String(entityId));
  if (field) parts.push(field);
  return parts.filter(Boolean).join(".");
}

// Entity-type → coarse area tag. Area drives the member permission filter
// (src/lib/audit/areaPermissions.js) and the Actor/Area dropdowns in the
// Activity Log UI.
export const ENTITY_TO_AREA = {
  // Finance (dashboard-centric)
  month: "finance",
  expense: "finance",
  income_item: "finance",
  delivery_override: "finance",
  order_expense: "finance",
  recurring_override: "finance",

  // Settings / brand-level (available today, currency, etc.)
  brand: "finance",

  // Marketplaces + products + groups + modifiers
  marketplace: "marketplaces",
  product: "marketplaces",
  product_group: "marketplaces",
  modifier: "marketplaces",
  fbm_setting: "marketplaces",

  // Orders / containers / payment terms
  order: "orders",
  payment_term: "orders",
  container_batch: "orders",
  container_line: "orders",

  // Recurring items (income + expenses list management)
  recurring: "recurring",

  // Decisions
  decision: "decisions",

  // Inventory pools
  pool: "pools",

  // Attachments — show to whoever can see the underlying entity. Area is
  // "finance" by default; the audit writer can override via details.area.
  attachment: "finance",

  // Team-level (owner-only)
  member_invited: "team",
  member_accepted: "team",
  permissions_updated: "team",
  whitelabel_updated: "team",
  contact_message: "team",
};

/** Resolve an area tag from either the audit row's `area` column or by
 *  falling back to entity_type. Always returns a string. */
export function resolveArea(row) {
  if (row?.area) return row.area;
  return ENTITY_TO_AREA[row?.entity_type] || "other";
}

// Friendly display labels for the Area dropdown filter.
export const AREA_LABELS = {
  finance:      "💰 Finance",
  marketplaces: "🛒 Marketplaces",
  orders:       "📦 Orders",
  recurring:    "🔁 Recurring",
  decisions:    "🎯 Decisions",
  pools:        "📚 Pools",
  team:         "👥 Team",
  other:        "• Other",
};
